import React, { useState } from "react";

import Loading from "components/Loading";
import Panel from "../Panel";

import { Link } from "react-router-dom";
import { useRoutes } from "utils/routes";
import { useSetAlertPolicyQuery } from "./gql/Query.generated";
import { useUpdateOrganizationMembershipMutation } from "components/MemberEdit/gql/Mutation.generated";
import { useSetFlash } from "components/WithFlashes";
import CreatableSelect from "components/CreatableSelect";

type AlertPolicyType = {
  value: string;
  label: string;
};

const SetAlertPolicy = ({
  organizationSlug,
  membershipId,
}: {
  organizationSlug: string;
  membershipId: string;
}) => {
  const { data, loading, error, refetch } = useSetAlertPolicyQuery({
    variables: { organizationSlug, membershipId },
  });
  if (loading || error) {
    return <Loading error={!!error} />;
  }

  const alertPolicy = {
    value: data.getOrganizationMembershipDetails.alertPolicy.id,
    label: data.getOrganizationMembershipDetails.alertPolicy.policyName,
  };
  const alertPolicyOptions = data.getAlertPolicies.map((policy) => {
    return {
      value: policy.id,
      label: policy.policyName,
    };
  });

  return (
    <SetAlertPolicyContent
      organizationSlug={organizationSlug}
      membershipId={membershipId}
      currentAlertPolicy={alertPolicy}
      alertPolicyOptions={alertPolicyOptions}
      refetchAlertPolicy={refetch}
    />
  );
};

const SetAlertPolicyContent = ({
  organizationSlug,
  membershipId,
  currentAlertPolicy,
  alertPolicyOptions,
  refetchAlertPolicy,
}: {
  organizationSlug: string;
  membershipId: string;
  currentAlertPolicy: AlertPolicyType;
  alertPolicyOptions: AlertPolicyType[];
  refetchAlertPolicy: () => void;
}) => {
  const [alertPolicy, setAlertPolicy] = useState(currentAlertPolicy);
  const setFlash = useSetFlash();
  const { organizationAlertPolicy } = useRoutes();
  const [updateOrganizationMembership] =
    useUpdateOrganizationMembershipMutation();
  const handleSubmit = () => {
    updateOrganizationMembership({
      variables: {
        membershipId: membershipId,
        alertPolicyId: alertPolicy.value,
      },
      onError: (error) => {
        setFlash("error", `Failed to update alert policy: ${error.message}`);
      },
      onCompleted: (data) => {
        setFlash(
          "success",
          `Updated alert policy for ${data.updateOrganizationMembership.email}`,
        );
        refetchAlertPolicy();
      },
    });
  };

  return (
    <Panel title="Alert Policy">
      <div className="m-[10px]">
        <strong>
          Current Policy:{" "}
          <Link
            to={organizationAlertPolicy(
              organizationSlug,
              currentAlertPolicy.value,
            )}
          >
            {currentAlertPolicy.label}
          </Link>
        </strong>
        <div className="help-block">
          This will only change which alert policy applies to your user in this
          organization.
        </div>
        <div className="mb-4 max-w-[20em] min-w-[10em]">
          <CreatableSelect
            value={alertPolicy}
            options={alertPolicyOptions}
            onChange={(newPolicy) => setAlertPolicy(newPolicy)}
          />
        </div>
        <button
          type="submit"
          className="btn btn-success"
          onClick={handleSubmit}
        >
          Change
        </button>
      </div>
    </Panel>
  );
};

export default SetAlertPolicy;
